import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Eye, Edit, Trash2, Pause, Play, Plus } from 'lucide-react';
import { carsAPI } from '../api';
import { toast } from 'sonner';
import LoadingSpinner from './LoadingSpinner';

const MyCars = () => {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await carsAPI.getMine();
      setCars(res.data || []);
    } catch (e) {
      toast.error('Error cargando tus autos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleAvailable = async (car) => {
    try {
      await carsAPI.update(car.id, { available: !car.available });
      toast.success(car.available ? 'Auto pausado' : 'Auto publicado');
      load();
    } catch (e) {
      toast.error('Error actualizando');
    }
  };

  const remove = async (car) => {
    if (!window.confirm(`¿Eliminar "${car.name}"? Esto no se puede deshacer.`)) return;
    try {
      await carsAPI.delete(car.id);
      toast.success('Auto eliminado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  if (loading) return <div className="py-8 flex justify-center"><LoadingSpinner /></div>;

  if (cars.length === 0) {
    return (
      <Card data-testid="my-cars-empty">
        <CardContent className="py-12 text-center">
          <p className="text-gray-600 mb-4">Aún no tienes autos publicados</p>
          <Button onClick={() => navigate('/mobilitier/add-car')} data-testid="empty-add-car-btn">
            <Plus className="w-4 h-4 mr-2" /> Publicar mi primer auto
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-cars-grid">
      {cars.map(car => (
        <Card key={car.id} className="overflow-hidden hover:shadow-lg transition" data-testid={`my-car-${car.id}`}>
          <div className="relative aspect-video bg-gray-100">
            {car.images?.[0] ? (
              <img src={car.images[0]} alt={car.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Sin imagen</div>
            )}
            <div className="absolute top-2 right-2">
              {car.available ? (
                <Badge className="bg-green-600">Publicado</Badge>
              ) : (
                <Badge variant="secondary" className="bg-orange-500 text-white">Pausado</Badge>
              )}
            </div>
          </div>
          <CardContent className="p-4 space-y-2">
            <div>
              <h3 className="font-semibold text-base truncate">{car.name}</h3>
              <p className="text-sm text-gray-600">{car.location}</p>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-bold text-blue-600">${car.price_per_day} MXN/día</span>
              <span className="text-gray-500 text-xs">{car.bookings_count || 0} reservas</span>
            </div>
            <div className="flex gap-1 pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/car/${car.id}`)}
                title="Ver"
                data-testid={`view-car-btn-${car.id}`}
              >
                <Eye className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/mobilitier/edit-car/${car.id}`)}
                title="Editar"
                data-testid={`edit-car-btn-${car.id}`}
              >
                <Edit className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant={car.available ? 'secondary' : 'default'}
                onClick={() => toggleAvailable(car)}
                className={`flex-1 ${car.available ? '' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                data-testid={`toggle-car-btn-${car.id}`}
              >
                {car.available ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pausar</> : <><Play className="w-3.5 h-3.5 mr-1" /> Publicar</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => remove(car)}
                className="text-red-600 hover:bg-red-50"
                title="Eliminar"
                data-testid={`delete-car-btn-${car.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default MyCars;
